import { Box, Button } from '@mui/material';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Localized, PoolFormattedMessage } from '@synergy/components';
import {
    PaloColumn,
    PaloConfirmationDialog,
    PaloListView,
    PaloRowAction,
    PaloStatusPill,
    PaloThumb,
} from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { SponsorBannerStatus, getSponsorBannerTranslationId } from '@synergy/models';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as SupplierSpecialOffersActions from '../store/supplier-special-offers-actions';
import * as SupplierSpecialOffersListActions from '../supplier-special-offers-list/store/supplier-special-offers-list-actions';
import * as SupplierSpecialOffersListSelectors from '../supplier-special-offers-list/store/supplier-special-offers-list-selectors';
import { PaloFilterPanel } from '../../../../common/components/palo/palo-filter-panel';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { SupplierSpecialOffer } from '../models/supplier-special-offer';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../../common/components/palo/use-palo-list-views';
import { usePaloSort } from '../../../../common/components/palo/use-palo-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const statusOf = (offer: SupplierSpecialOffer): SponsorBannerStatus => {
    const now = new Date();
    const validFrom = new Date(offer.validFrom);
    validFrom.setHours(0, 0, 0, 0);
    const validTo = new Date(offer.validTo);
    validTo.setHours(23, 59, 59, 999);

    return now >= validFrom && now <= validTo ? SponsorBannerStatus.Active : SponsorBannerStatus.Inactive;
};

const columns: PaloColumn<SupplierSpecialOffer>[] = [
    {
        key: 'status',
        type: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        cell: offer => {
            const status = statusOf(offer);

            return (
                <PaloStatusPill
                    status={status}
                    label={<FormattedMessage id={getSponsorBannerTranslationId(status)} />}
                />
            );
        },
    },
    {
        key: 'logo',
        type: 'icon',
        head: <FormattedMessage id="app.table.column.logo" />,
        cell: offer => (
            <PaloThumb
                src={(offer.image ?? offer.supplier.image)?.['184x79']}
                alt={offer.supplier.name}
                width={72}
                height={26}
                sx={{ objectFit: 'contain' }}
            />
        ),
    },
    {
        key: 'supplier',
        link: true,
        type: 'name',
        sortKey: 'supplier',
        head: <FormattedMessage id="app.table.column.supplier" />,
        cell: offer => offer.supplier.name ?? '',
    },
    {
        key: 'title',
        type: 'name',
        sortKey: 'title',
        head: <FormattedMessage id="app.table.column.title" />,
        cell: offer => <Localized value={offer.title} />,
    },
    {
        key: 'validFrom',
        type: 'date',
        sortKey: 'validFrom',
        head: <FormattedMessage id="app.table.column.validFrom" />,
        cell: offer => <FormattedDate value={offer.validFrom} year="numeric" month="short" day="2-digit" />,
    },
    {
        key: 'validTo',
        type: 'date',
        head: <FormattedMessage id="app.table.column.validTo" />,
        cell: offer => <FormattedDate value={offer.validTo} year="numeric" month="short" day="2-digit" />,
    },
];

export const PaloSupplierSpecialOffersList = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('supplier-special-offers');
    const columnFilter = usePaloColumnFilters('supplier-special-offers');
    const intl = useIntl();
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloSort('validFrom', 'desc');
    const requesting = useAppSelector(SupplierSpecialOffersListSelectors.supplierSpecialOffersListRequestingSelector);
    const paginated = useAppSelector(SupplierSpecialOffersListSelectors.supplierSpecialOffersListItemsSelector);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const [toDelete, setToDelete] = useState<number | null>(null);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(SupplierSpecialOffersListActions.fetchSupplierSpecialOffersList(params));
    }, [dispatch, params]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const detailHref = (offer: SupplierSpecialOffer): string =>
        ROUTES.contentManagement.supplierSpecialOffers.detail.replace(':id', offer.id.toString());

    const openDetail = (offer: SupplierSpecialOffer): void => navigate(detailHref(offer));

    const handleDelete = (): void => {
        if (toDelete) {
            dispatch(SupplierSpecialOffersActions.deleteSupplierSpecialOffers(toDelete));
            setToDelete(null);
        }
    };

    const rowActions = (offer: SupplierSpecialOffer): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(offer) },
        { label: 'Löschen', onClick: () => setToDelete(offer.id) },
    ];

    const headerActions = (
        <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`${ROUTES.contentManagement.supplierSpecialOffers.list}/new`)}
        >
            <FormattedMessage id="app.contentManagement.supplierSpecialOffers.new" />
        </Button>
    );

    return (
        <PaloListView<SupplierSpecialOffer>
            pageTitle={intl.formatMessage({ id: 'app.contentManagement.supplierSpecialOffers' })}
            sort={sort}
            onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
            title={<FormattedMessage id="app.contentManagement.supplierSpecialOffers" />}
            headerActions={headerActions}
            filters={
                <>
                    <PaloFilterPanel listKey="supplier-special-offers" />
                    {savedFiltersMenu}
                </>
            }
            columns={orderColumns(columns)}
            columnFilter={columnFilter}
            onMoveColumn={moveColumn}
            hiddenColumnKeys={hiddenColumnKeys}
            shownColumnKeys={shownColumnKeys}
            onToggleColumn={toggleColumn}
            items={paginated.items}
            getRowId={offer => offer.id}
            requesting={requesting}
            emptyLabel="Keine Sonderangebote."
            rowHref={detailHref}
            rowActions={rowActions}
            pagination={{
                count: paginated.total,
                page,
                limit,
                onPageChange: newPage => updateParams({ page: String(newPage) }),
                onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
            }}
        >
            <PaloConfirmationDialog
                isVisible={!!toDelete}
                onConfirm={handleDelete}
                onCancel={() => setToDelete(null)}
                title={<FormattedMessage id="app.contentManagement.supplierSpecialOffers.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
            >
                <PoolFormattedMessage
                    id="app.contentManagement.supplierSpecialOffers.deleteWarning"
                    values={{ name: paginated.items.find(item => item.id === toDelete)?.title[currentLocale] }}
                />
            </PaloConfirmationDialog>
        </PaloListView>
    );
};
